// components/Pricing.jsx — Dynamic pricing tiers section

import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { SectionWrapper, SectionTitle, Card, Button, Badge } from "./UI";

export default function Pricing({ content, t, dark, hasBg = false, lang }) {
  const isEs = lang === "es";
  const { tiers, numberOfTiersToShow } = content;
  const visibleTiers = tiers.slice(0, numberOfTiersToShow);

  return (
    <SectionWrapper id="pricing" dark={dark} bg={hasBg ? "alt" : "default"}>
      <SectionTitle title={t.pricing.sectionTitle} subtitle={t.pricing.sectionSubtitle} dark={dark} />

      {/* Desktop: grid | Mobile: swiper */}
      <div className="hidden md:grid grid-cols-3 gap-6 items-stretch">
        {visibleTiers.map((tier) => {
          const name    = isEs ? (tier.nameEs    || tier.name)    : tier.name;
          const bullets = isEs ? (tier.bulletsEs || tier.bullets) : tier.bullets;

          return (
            <div key={tier.id} className={`flex flex-col relative transition-all duration-300 ${tier.recommended ? "md:-translate-y-2 md:scale-[1.02]" : ""}`}>
              {tier.recommended && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                  <span className="bg-[#A3E635] text-[#0F172A] text-xs font-bold px-4 py-1.5 rounded-full shadow-lg shadow-[#A3E635]/30 whitespace-nowrap">
                    ⭐ {t.pricing.recommended}
                  </span>
                </div>
              )}

              <Card dark={dark} className={`p-8 flex flex-col h-full ${tier.recommended ? "border-[#A3E635]/50 shadow-2xl shadow-[#A3E635]/10" : ""}`}>
                <h3 className={`text-xl font-bold mb-1 ${tier.recommended ? "text-[#A3E635]" : ""}`}>{name}</h3>

                <div className="mb-6">
                  {tier.originalPrice && (
                    <span className="text-2xl font-bold line-through mr-2 text-red-400">{tier.originalPrice}</span>
                  )}
                  <span className="text-4xl font-bold">{tier.price}</span>
                  <span className={`text-sm ml-1 ${dark ? "text-white/40" : "text-[#0F172A]/40"}`}>
                    {isEs ? "/ proyecto" : "/ project"}
                  </span>
                  {tier.originalPrice && (
                    <span className="ml-2 text-xs font-bold bg-[#A3E635]/15 text-[#A3E635] px-2 py-0.5 rounded-full">
                      {isEs ? "Oferta" : "Sale"}
                    </span>
                  )}
                </div>

                <div className={`h-px w-full mb-6 ${dark ? "bg-white/10" : "bg-[#E2E8F0]"}`} />

                <ul className="space-y-3 flex-1 mb-8">
                  {bullets.map((bullet, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      <span className="text-[#A3E635] font-bold mt-0.5 shrink-0">✓</span>
                      <span className={dark ? "text-white/70" : "text-[#0F172A]/70"}>{bullet}</span>
                    </li>
                  ))}
                </ul>

                <a href="#contact">
                  <Button
                    variant={tier.recommended ? "primary" : "secondary"}
                    size="lg"
                    className={`w-full ${!tier.recommended && dark ? "border-white/20 text-white hover:border-[#A3E635] hover:text-[#A3E635]" : ""}`}
                  >
                    {t.pricing.getStarted}
                  </Button>
                </a>
              </Card>
            </div>
          );
        })}
      </div>

      {/* Mobile swiper */}
      <div className="md:hidden -mx-4 overflow-x-hidden pt-6">
        <style>{".pricing-swiper .swiper-pagination-bullet { background: #A3E635; } .pricing-swiper .swiper-pagination-bullet-active { background: #A3E635; }"}</style>
        <Swiper
          className="pricing-swiper pb-10"
          modules={[Pagination]}
          pagination={{ clickable: true }}
          spaceBetween={12}
          slidesPerView={1.2}
          slidesOffsetBefore={16}
          slidesOffsetAfter={16}
        >
          {visibleTiers.map((tier) => {
            const name    = isEs ? (tier.nameEs    || tier.name)    : tier.name;
            const bullets = isEs ? (tier.bulletsEs || tier.bullets) : tier.bullets;
            return (
              <SwiperSlide key={tier.id} className="!h-auto pb-2 pt-6">
                <div className={`flex flex-col relative transition-all duration-300`}>
                  {tier.recommended && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                      <span className="bg-[#A3E635] text-[#0F172A] text-xs font-bold px-4 py-1.5 rounded-full shadow-lg shadow-[#A3E635]/30 whitespace-nowrap">
                        ⭐ {t.pricing.recommended}
                      </span>
                    </div>
                  )}
                  <Card dark={dark} className={`p-8 flex flex-col h-full ${tier.recommended ? "border-[#A3E635]/50 shadow-2xl shadow-[#A3E635]/10" : ""}`}>
                    <h3 className={`text-xl font-bold mb-1 ${tier.recommended ? "text-[#A3E635]" : ""}`}>{name}</h3>
                    <div className="mb-6">
                      {tier.originalPrice && (
                        <span className="text-2xl font-bold line-through mr-2 text-red-400">{tier.originalPrice}</span>
                      )}
                      <span className="text-4xl font-bold">{tier.price}</span>
                      <span className={`text-sm ml-1 ${dark ? "text-white/40" : "text-[#0F172A]/40"}`}>
                        {isEs ? "/ proyecto" : "/ project"}
                      </span>
                      {tier.originalPrice && (
                        <span className="ml-2 text-xs font-bold bg-[#A3E635]/15 text-[#A3E635] px-2 py-0.5 rounded-full">
                          {isEs ? "Oferta" : "Sale"}
                        </span>
                      )}
                    </div>
                    <div className={`h-px w-full mb-6 ${dark ? "bg-white/10" : "bg-[#E2E8F0]"}`} />
                    <ul className="space-y-3 flex-1 mb-8">
                      {bullets.map((bullet, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm">
                          <span className="text-[#A3E635] font-bold mt-0.5 shrink-0">✓</span>
                          <span className={dark ? "text-white/70" : "text-[#0F172A]/70"}>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                    <a href="#contact">
                      <Button
                        variant={tier.recommended ? "primary" : "secondary"}
                        size="lg"
                        className={`w-full ${!tier.recommended && dark ? "border-white/20 text-white hover:border-[#A3E635] hover:text-[#A3E635]" : ""}`}
                      >
                        {t.pricing.getStarted}
                      </Button>
                    </a>
                  </Card>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </SectionWrapper>
  );
}
